import { useQueryClient } from "@tanstack/react-query"
import { useFindOrCreateOfferMutation } from "@apis/offers/useOffers"
import { useSaveShopperMutation } from "@apis/shoppers/useShoppers"
import type { IAccount } from "@apis/accounts/types"
import { accountKeys } from "@apis/accounts/queryKeys"
import type { TCustomOfferFormValues } from "../CustomOfferForm"
import { useAuthUserDetails } from "@apis/users/useUsers"
import { useSaveOrderMutation } from "@apis/orders/useOrders"
import { toast } from "sonner"

type TCustomOfferSubmitProps = {
  account?: IAccount
  case_guid: string
  setShowChangeOfferForm: (showForm: boolean) => void
}
export const useCustomOfferSubmit = ({
  account,
  case_guid,
  setShowChangeOfferForm,
}: TCustomOfferSubmitProps) => {
  const { data: userInfo } = useAuthUserDetails()
  const queryClient = useQueryClient()

  const getOrCreateOfferMutation = useFindOrCreateOfferMutation()
  const saveShopperMutation = useSaveShopperMutation()
  const saveOrderMutation = useSaveOrderMutation()

  const customOfferOnSubmit = async (formValues: TCustomOfferFormValues) => {
    if (!account) {
      throw new Error("Account is required for custom offer submission")
    }

    try {
      // This flow will eventually be moved to a new endpoint in Garcon.
      // Until then, we need to use the mutateAsync approach to make each call in order.
      // Step 1: Create offer
      const offer = await getOrCreateOfferMutation.mutateAsync({
        data: {
          market_code: account.market_code,
          revenue_class_code: account.revenue_class_code,
          plan_code: formValues.plan_code as string,
          channel_code: "RETENTION",
          market_rate_id: Number(formValues.market_rate_id),
        },
      })

      if (!offer)
        throw new Error("Failed to generate custom offer for account.")

      // Step 2: Build shopper
      const shopper = await saveShopperMutation.mutateAsync({
        shopper_guid: "",
        data: {
          prospect_guid: account.prospect_guid,
          channel_code: "RETENTION",
          revenue_class_code: account.revenue_class_code,
          channel_partner_code: "BRAND",
          source_code: userInfo?.source_code || "INSPIRE",
          initial_sale_dt: new Date().toISOString(),
          agent_id: userInfo?.email, // make sure this is populated
          first_name: account.first_name,
          last_name: account.last_name,
          contact_first_name: account.contact_first_name,
          contact_last_name: account.contact_last_name,
          contact_relationship: account.contact_relationship,
          title: account.title,
          business_name: account.business_name,
          email: account.email,
          primary_phone: account.primary_phone,
          account_number: account.account_number,
          ack_can_text: true,
          account_num_provider: account.account_num_provider,
          offer_code: offer?.offer_code,
          service_address_id: account?.service_address_id,
          mailing_address_id: account?.mailing_address_id,
          shipping_address_id: account?.shipping_address_id,
        },
      })

      if (!shopper) throw new Error("Failed to save shopper.")

      // Step 3: Build order
      const order = await saveOrderMutation.mutateAsync({
        body: {
          prospect_guid: account.prospect_guid,
          shopper_guid: shopper.shopper_guid as string,
          channel_code: "RETENTION",
          revenue_class_code: account.revenue_class_code,
          channel_partner_code: shopper.channel_partner_code as string,
          source_code: userInfo?.source_code || "INSPIRE",
          account_number: account.account_number,
          offer_code: shopper.offer_code,
          energy_product_code: offer.energy_product_code,
          market_rate_id: offer.market_rate_id,
          sale_dt: new Date().toISOString(),
          agent_id: userInfo?.email as string,
          first_name: shopper.first_name,
          last_name: shopper.last_name,
          business_name: shopper.business_name,
          contact_relationships: shopper.contact_relationships,
          contact_first_name: shopper.contact_first_name,
          contact_last_name: shopper.contact_last_name,
          title: shopper.title,
          email: shopper.email,
          primary_phone: shopper.primary_phone,
          service_address_id: shopper.service_address_id,
          mailing_address_id: shopper.mailing_address_id,
          shipping_address_id: shopper.shipping_address_id,
          sales_location_code: shopper.sales_location_code,
          partner_member_number: shopper.partner_member_number,
          ack_can_text: shopper.ack_can_text,
          account_num_provider: shopper.account_num_provider,
          source: "custom_retention",
          requested_start_dt: new Date().toISOString(),
          membership_profile_id: account.membership_profile_id,
          case_guid,
          conversion_medium: "INSPIRE_CRM",
        },
      })

      if (!order) throw new Error("Failed to change offer for account.")

      queryClient.invalidateQueries({
        queryKey: accountKeys.accountEvents(
          account.prospect_guid,
          String(account.account_id),
        ),
      })
      queryClient.invalidateQueries({
        queryKey: accountKeys.accountNotifications(
          account.prospect_guid,
          String(account.account_id),
        ),
      })
      toast.success("Successfully created custom offer for account!")
      setShowChangeOfferForm(false)
    } catch (error) {
      toast.error(`Failed to generate custom offer for account. ${error}`)
    }
  }

  return {
    customOfferOnSubmit,
    submitIsPending:
      getOrCreateOfferMutation.isPending ||
      saveShopperMutation.isPending ||
      saveOrderMutation.isPending,
  }
}
